import { Box, CardContent, CircularProgress, Divider, TextField } from '@mui/material'
import { debounce } from 'lodash'
import { useTranslate } from 'ra-core'
import React, { useCallback, useEffect, useState } from 'react'
import {
  ChipField,
  DateField,
  FunctionField,
  ImageField,
  List,
  ReferenceArrayField,
  ReferenceManyField,
  SimpleShowLayout,
  SingleFieldList,
  TabbedShowLayout,
  useGetOne,
  useNotify,
  useRecordContext,
  useResourceContext,
} from 'react-admin'

import { useApi } from '../../api/apiProvider'
import AdvancedArrayField from '../../components/AdvancedArrayField'
import AdvancedBooleanField from '../../components/AdvancedBooleanField'
import AdvancedEnumTextField from '../../components/AdvancedEnumTextField'
import AdvancedNumberField from '../../components/AdvancedNumberField'
import AdvancedShow from '../../components/AdvancedShow'
import AdvancedTextField from '../../components/AdvancedTextField'
import AdvancedUrlField from '../../components/AdvancedUrlField'
import GroupAccessSummary from '../../components/GroupAccessSummary'
import OperationalStatusSection from '../../components/OperationalStatusSection'
import PeriodField from '../../components/PeriodField'
import PriceField from '../../components/PriceField'
import Scheduler from '../../components/Scheduler'
import ShowCardHeader from '../../components/ShowCardHeader'
import UnitField from '../../components/UnitField'
import VehicleLockField from '../../components/VehicleLockField'
import VehicleMapField from '../../components/VehicleMapField'
import { INCIDENT_REPORT_STATUS_VALIDATED } from '../../config/damages'
import {
  SYSTEM_PERMISSION_LIST,
  SYSTEM_PERMISSION_PATCH,
  SYSTEM_PERMISSION_POSITION,
  SYSTEM_PERMISSION_PRIVATE_DATA,
  SYSTEM_PERMISSION_READ,
} from '../../config/permissions'
import { useCommonStyles } from '../../config/theme'
import {
  VEHICLE_ENERGY_TYPE_ELECTRIC,
  VEHICLE_ENERGY_TYPE_FUEL,
  VEHICLE_GATEWAYS,
  VEHICLE_SPECIAL_FIELD_CODE_CHARGING_INSTRUCTIONS,
  VEHICLE_SPECIAL_FIELD_CODE_FUEL_CARD_PIN,
  VEHICLE_SPECIAL_FIELD_CODE_FUELING_INFO,
  VEHICLE_SPECIAL_FIELD_CODE_PARKING_SPOT,
  VEHICLE_SPECIAL_FIELDS,
  VEHICLE_TYPE_CAR_BLUEPRINT_SETS,
} from '../../config/vehicles'
import { useIsFieldPermissionDenied, useResourcePermissions } from '../../domain/permissions'
import { isVehicleCar, isVehicleChargeableCar, isVehicleElectricCar, isVehicleFuelCar } from '../../domain/vehicles'
import { useSmallScreen } from '../../utils/theme'
import bookingsConfig from '../bookings/config'
import { BookingsListLayout } from '../bookings/list'
import { BookingShowLayout } from '../bookings/show'
import { CardTitle } from '../common'
import { ListReference } from '../common/list'
import { ShowActions, ShowReference, ShowReferenceLinks, ShowSubSectionTitleCard, useShowStyles } from '../common/show'
import useGroupMembershipRecord from '../common/useGroupMembershipRecord'
import damageReportsConfig from '../damageReports/config'
import { IncidentReportsListLayout } from '../damageReports/list'
import HubField from '../hubs/field'
import OrganisationField from '../organisations/field'
import vehicleDetailsConfig from '../vehicleDetails/config'
import vehicleTelematicsConfig from '../vehicleTelematics/config'
import { VehicleTelematicsFilters, VehicleTelematicsMapView } from '../vehicleTelematics/list'
import vehicleUnavailabilitiesConfig from '../vehicleUnavailabilities/config'
import { VehicleUnavailabilitiesListLayout } from '../vehicleUnavailabilities/list'

const VehicleSpecialField = ({ code, label }) => {
  const record = useRecordContext()
  return (
    <FunctionField
      label={label}
      render={() => {
        const detail = record.details?.find((item) => item.code === code)
        if (code === VEHICLE_SPECIAL_FIELD_CODE_PARKING_SPOT && record.parking_spot) {
          // Show both static and dynamic parking spots if present
          return `${detail?.value || ''} ${record.parking_spot}`.trim()
        }
        return detail?.value ?? 'n/a'
      }}
    />
  )
}

export const VehicleShowLayout = ({
  title = 'resources.vehicles.show.titles.main',
  hasRedirect = false,
  excluded = [],
  ...props
}) => {
  const recordContext = useRecordContext(props)
  const [canReadGroups, canListGroups] = useResourcePermissions(
    'vehicle-groups',
    SYSTEM_PERMISSION_READ,
    SYSTEM_PERMISSION_LIST,
  )
  const { record } = useGroupMembershipRecord('vehicles', 'vehicle_group_ids', recordContext)
  const resource = useResourceContext()
  const { data: vehicleDetails } = useGetOne(vehicleDetailsConfig.name, { id: record.id })

  const notify = useNotify()
  const translate = useTranslate()
  const showClasses = useShowStyles()
  const commonClasses = useCommonStyles()
  const isSmallScreen = useSmallScreen()

  const [hasPatch, hasPosition, hasPrivateData] = useResourcePermissions(
    resource,
    SYSTEM_PERMISSION_PATCH,
    SYSTEM_PERMISSION_PRIVATE_DATA,
    SYSTEM_PERMISSION_POSITION,
  )
  const hasPositionForVehicles = hasPosition || hasPrivateData
  const hasGatewayLink = !useIsFieldPermissionDenied({ action: SYSTEM_PERMISSION_READ, source: 'gateway.link' })

  const [notes, setNotes] = useState(null)
  const [notesBeingSaved, setNotesBeingSaved] = useState(false)
  const debouncedSetNotes = useCallback(debounce(setNotes, 2000), []) // eslint-disable-line react-hooks/exhaustive-deps
  const [runSaveNotesQuery] = useApi(`/vehicles/${record.id}`, {
    method: 'PATCH',
    body: JSON.stringify({ notes }),
    onSuccess: () => {
      setNotesBeingSaved(false)
    },
    onFailure: (error) => {
      setNotesBeingSaved(false)
      notify(translate('resources.vehicles.show.couldNotSaveNotes', { errorMessage: error.message }), {
        type: 'error',
      })
    },
  })

  useEffect(() => {
    if (vehicleDetails) {
      setNotes(vehicleDetails.notes)
    }
  }, [JSON.stringify(vehicleDetails), setNotes]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Don't save notes on initial render
    if (notes === null || !vehicleDetails || notes === vehicleDetails.notes) {
      return
    }
    runSaveNotesQuery()
  }, [notes, vehicleDetails?.notes]) // eslint-disable-line react-hooks/exhaustive-deps

  const simpleShowLayoutCommonProps = { record, sx: showClasses.fieldContainer }
  const operationalStatusSectionProps = { data: vehicleDetails?.status_timeline }

  return (
    <>
      {title && <ShowCardHeader hasRedirect={hasRedirect} record={record} title={title} />}
      {vehicleDetails && (
        <>
          <div style={isSmallScreen ? null : showClasses.row}>
            <div style={{ flex: 1 }}>
              <ShowSubSectionTitleCard text="identification" />
              <div style={isSmallScreen ? null : showClasses.row}>
                <div style={{ flex: 1 }}>
                  <SimpleShowLayout {...simpleShowLayoutCommonProps}>
                    <ImageField
                      source="picture"
                      title="model"
                      label={false}
                      sx={{
                        '& img': {
                          width: 'auto !important',
                          maxHeight: '6rem !important',
                          marginTop: '9px !important',
                        },
                      }}
                    />
                    <AdvancedEnumTextField source="type" />
                    {isVehicleCar(record) && (
                      <AdvancedTextField source="blueprint_set" map={VEHICLE_TYPE_CAR_BLUEPRINT_SETS} />
                    )}
                    <AdvancedTextField source="brand" />
                    <AdvancedTextField source="model" />
                    <AdvancedTextField source="year" />
                    <AdvancedTextField source="designation" />
                    {isVehicleCar(record) && <AdvancedTextField source="identification_number" />}
                  </SimpleShowLayout>
                </div>
              </div>
              <ShowSubSectionTitleCard text="status" />
              <SimpleShowLayout {...simpleShowLayoutCommonProps} data-cy="statusShowLayout">
                <UnitField source="autonomy" />
                <UnitField source="mileage" />
                <UnitField source="next_maintenance_mileage" />
                <DateField source="next_maintenance_date" emptyText="n/a" />
                {isVehicleCar(record) && [
                  <UnitField source="remaining_oil_life" formatValue={(v) => v.toFixed(0)} />,
                  <UnitField source="battery_voltage" formatValue={(v) => v.toFixed(1)} unit="V" />,
                  <AdvancedArrayField source="dtcs" />,
                ]}
                <VehicleLockField source="locked" />
                {isVehicleCar(record) && (
                  <AdvancedBooleanField source="driving_enabled" trueIcon={null} falseIcon={null} />
                )}
              </SimpleShowLayout>
              <ShowActions
                allowedActions={vehicleDetails.allowed_actions}
                helperText={translate('resources.vehicles.actions.helperText')}
              />
              {isSmallScreen && <OperationalStatusSection {...operationalStatusSectionProps} />}
              {(isVehicleCar(record) || record.color) && (
                <>
                  <ShowSubSectionTitleCard text="characteristics" />
                  <SimpleShowLayout {...simpleShowLayoutCommonProps}>
                    <AdvancedEnumTextField source="color" />
                    {isVehicleCar(record) && <AdvancedEnumTextField source="transmission_type" />}
                    {isVehicleCar(record) && <AdvancedBooleanField source="keyless" trueIcon={null} falseIcon={null} />}
                    {isVehicleCar(record) && <AdvancedNumberField source="number_of_seats" />}
                    {isVehicleCar(record) && <AdvancedNumberField source="number_of_doors" />}
                    {isVehicleCar(record) && <UnitField source="volume" unit="m³" />}
                    {isVehicleCar(record) && <PriceField source="value" />}
                    {isVehicleCar(record) && <AdvancedEnumTextField source="energy_type" />}
                    {record.energy_type === VEHICLE_ENERGY_TYPE_ELECTRIC && <UnitField source="maximum_autonomy" />}
                    {isVehicleChargeableCar(record) && (
                      <VehicleSpecialField
                        code={VEHICLE_SPECIAL_FIELD_CODE_CHARGING_INSTRUCTIONS}
                        label={VEHICLE_SPECIAL_FIELDS[VEHICLE_SPECIAL_FIELD_CODE_CHARGING_INSTRUCTIONS]}
                      />
                    )}
                    {isVehicleFuelCar(record) && <AdvancedEnumTextField source="combustion_type" />}
                    {isVehicleFuelCar(record) && <UnitField source="tank_size" unit="L" />}
                    {isVehicleFuelCar(record) && <UnitField source="average_consumption" unit="L/100km" />}
                    {isVehicleFuelCar(record) && (
                      <VehicleSpecialField
                        code={VEHICLE_SPECIAL_FIELD_CODE_FUELING_INFO}
                        label={VEHICLE_SPECIAL_FIELDS[VEHICLE_SPECIAL_FIELD_CODE_FUELING_INFO]}
                      />
                    )}
                  </SimpleShowLayout>
                </>
              )}
              <ShowSubSectionTitleCard text="parameters" />
              <SimpleShowLayout {...simpleShowLayoutCommonProps}>
                <OrganisationField />
                <HubField />
                <PeriodField
                  label="resources.common.fields.period"
                  startedOnSource="started_on"
                  endedOnSource="ended_on"
                  addTime
                />
                <AdvancedNumberField source="price_factor" />
                <PriceField source="security_deposit_amount" />
                {isVehicleCar(record) && <PriceField source="price_per_km" />}
                <VehicleSpecialField
                  code={VEHICLE_SPECIAL_FIELD_CODE_PARKING_SPOT}
                  label={VEHICLE_SPECIAL_FIELDS[VEHICLE_SPECIAL_FIELD_CODE_PARKING_SPOT]}
                />
                {isVehicleElectricCar(record) && (
                  <UnitField
                    source="charging_buffer"
                    unit="mymove.units.time.hours"
                    formatValue={(v) => v / 60}
                    shouldFormatUnit
                  />
                )}
                {isVehicleFuelCar(record) && (
                  <VehicleSpecialField
                    code={VEHICLE_SPECIAL_FIELD_CODE_FUEL_CARD_PIN}
                    label={VEHICLE_SPECIAL_FIELDS[VEHICLE_SPECIAL_FIELD_CODE_FUEL_CARD_PIN]}
                  />
                )}
                {!isVehicleCar(record) && (
                  <VehicleSpecialField
                    code={VEHICLE_SPECIAL_FIELD_CODE_CHARGING_INSTRUCTIONS}
                    label={VEHICLE_SPECIAL_FIELDS[VEHICLE_SPECIAL_FIELD_CODE_CHARGING_INSTRUCTIONS]}
                  />
                )}
              </SimpleShowLayout>
              {canReadGroups && canListGroups && Array.isArray(record.vehicle_group_ids) && (
                <SimpleShowLayout {...simpleShowLayoutCommonProps}>
                  <ReferenceArrayField source="vehicle_group_ids" reference="vehicle-groups">
                    <Box>
                      <SingleFieldList linkType={false} empty={translate('mymove.accessGroups.noGroups')}>
                        <ChipField source="name" sx={{ typography: 'body2' }} />
                      </SingleFieldList>
                      <GroupAccessSummary groupIds={record.vehicle_group_ids} resource="vehicles" />
                    </Box>
                  </ReferenceArrayField>
                </SimpleShowLayout>
              )}
              <ShowSubSectionTitleCard text="providerDetails" />
              <SimpleShowLayout {...simpleShowLayoutCommonProps}>
                <AdvancedBooleanField
                  source="installed"
                  trueIcon={null}
                  falseIcon={null}
                  trueLabel="resources.vehicles.enums.installed.true"
                  falseLabel="resources.vehicles.enums.installed.false"
                />
                <AdvancedBooleanField source="nfc_access_supported" trueIcon={null} falseIcon={null} />
                <AdvancedTextField source="gateway.code" map={VEHICLE_GATEWAYS} />
                <AdvancedTextField source="gateway.vehicle_id" />
                {hasGatewayLink && (
                  <AdvancedUrlField
                    record={vehicleDetails}
                    source="gateway.link"
                    text={translate('resources.vehicles.show.goToVehicleConsole')}
                  />
                )}
              </SimpleShowLayout>
            </div>
            <Box width="100%" maxWidth={isSmallScreen ? null : 480} sx={isSmallScreen ? {} : commonClasses.borderLeft}>
              {!isSmallScreen && <OperationalStatusSection {...operationalStatusSectionProps} />}
              {hasPositionForVehicles && (
                <>
                  <ShowSubSectionTitleCard text="location" />
                  <SimpleShowLayout
                    record={record}
                    sx={{
                      '& .RaSimpleShowLayout-row': { margin: 0, width: '100%' },
                      ...commonClasses.borderTop,
                      ...showClasses.map,
                    }}
                  >
                    <VehicleMapField label={false} />
                  </SimpleShowLayout>
                </>
              )}
              <CardContent sx={{ ...showClasses.subTitleContainer, ...commonClasses.borderTop }}>
                <CardTitle
                  size={17}
                  text={
                    <div style={commonClasses.flexRowCenterContainer}>
                      <div>{translate('resources.vehicles.fields.notes')}</div>
                      {hasPatch && notesBeingSaved && <CircularProgress size={18} style={{ marginLeft: 12 }} />}
                    </div>
                  }
                />
              </CardContent>
              {hasPatch ? (
                <SimpleShowLayout record={vehicleDetails} sx={commonClasses.borderTop}>
                  <TextField
                    key={vehicleDetails.id}
                    id="notes-textarea-input"
                    defaultValue={vehicleDetails.notes ?? ''}
                    onChange={(event) => {
                      if (event.target.value === notes) {
                        setNotesBeingSaved(false)
                        debouncedSetNotes.cancel()
                      } else {
                        setNotesBeingSaved(true)
                        debouncedSetNotes(event.target.value)
                      }
                    }}
                    sx={{ ...showClasses.textFieldWithFullWidth, width: 415 }}
                    placeholder={translate('resources.vehicles.notesPlaceholder')}
                    multiline
                  />
                </SimpleShowLayout>
              ) : (
                <SimpleShowLayout record={vehicleDetails} sx={showClasses.fieldContainer}>
                  <AdvancedTextField
                    label={false}
                    source="notes"
                    fallback={translate('resources.vehicles.notesPlaceholder')}
                    sx={{ whiteSpace: 'pre-wrap' }}
                  />
                </SimpleShowLayout>
              )}
            </Box>
          </div>
          <ShowReferenceLinks excluded={excluded} record={record} />
        </>
      )}
    </>
  )
}

const VehicleAside = () => {
  const record = useRecordContext()
  const { data: vehicleDetails } = useGetOne(vehicleDetailsConfig.name, { id: record.id })
  return (
    <>
      <List
        actions={null}
        empty={false}
        filter={{ id: [record.id] }}
        pagination={false}
        storeKey="vehicleTimeline"
        sx={{ marginTop: '24px', '& .RaList-main': { maxWidth: '100%' } }}
        title="&#32;"
      >
        <Scheduler disabledInputsSources={['organisation_id', 'hub_id', 'vehicle_id']} />
      </List>
      {vehicleDetails && (
        <ShowReference record={vehicleDetails} reference="bookings" source="current_booking_id">
          <BookingShowLayout
            excluded={['organisations', 'hubs', 'vehicles']}
            hasRedirect
            title="resources.common.show.currentBooking"
          />
        </ShowReference>
      )}
      <ListReference
        reference={vehicleUnavailabilitiesConfig.name}
        target="vehicle_id"
        sort={vehicleUnavailabilitiesConfig.options.defaultSort}
      >
        <VehicleUnavailabilitiesListLayout
          createProps={{
            defaultValues: { organisation_id: record.organisation_id, vehicle_id: record.id },
            disabledInputsSources: ['organisation_id', 'vehicle_id'],
          }}
          excluded={['organisations', 'vehicles']}
        />
      </ListReference>
      <ListReference
        reference="bookings"
        target="vehicle_id"
        sort={bookingsConfig.options.defaultSort}
        filter={bookingsConfig.options.defaultFilterValues}
      >
        <BookingsListLayout excluded={['vehicles']} />
      </ListReference>
      <ListReference
        reference="incident-reports"
        target="vehicle_id"
        sort={damageReportsConfig.options.defaultSort}
        filter={{ status: INCIDENT_REPORT_STATUS_VALIDATED }}
      >
        <IncidentReportsListLayout excluded={['organisations', 'hubs', 'vehicles']} />
      </ListReference>
    </>
  )
}

const VehicleMapTabContent = () => {
  const { hub_id } = useRecordContext()
  return (
    <>
      <Divider sx={{ margin: 0 }} />
      <ReferenceManyField
        label={false}
        perPage={10000}
        reference={vehicleTelematicsConfig.name}
        sort={vehicleTelematicsConfig.options.defaultSort}
        target="vehicle_id"
        filter={{ hub_id }}
      >
        <Box padding="0px 16px 16px">
          <VehicleTelematicsFilters isListView={false} />
          <Box maxHeight={600}>
            <VehicleTelematicsMapView height={600} isListView={false} />
          </Box>
        </Box>
      </ReferenceManyField>
    </>
  )
}

export default () => {
  const [hasReadForTelematics] = useResourcePermissions(vehicleTelematicsConfig.name, SYSTEM_PERMISSION_READ)
  return (
    <AdvancedShow aside={<VehicleAside />}>
      {hasReadForTelematics ? (
        <TabbedShowLayout>
          <TabbedShowLayout.Tab label="resources.vehicles.show.titles.main">
            <VehicleShowLayout title={false} />
          </TabbedShowLayout.Tab>
          <TabbedShowLayout.Tab label="resources.vehicles.show.titles.map" path="map">
            <VehicleMapTabContent />
          </TabbedShowLayout.Tab>
        </TabbedShowLayout>
      ) : (
        <VehicleShowLayout />
      )}
    </AdvancedShow>
  )
}
